import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, Button, Caption1, HistoryOfChangesIcon } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import UserLink from "~/components/UserLink/UserLink";
import { useHistoryOfChanges } from "~/repository";
import { canSeeTimeEstimates } from "~/store/selectors/selector.users";
import { descSortCompare, taskClass } from "~/utils/utils.jsx";
import Ago from "../component.ago.jsx";

const HistoryItem = ({ historyItem }) => {
  const { t } = useTranslation();

  return (
    <li>
      <ul
        className="object_history_event_changes"
        data-testid="history-list-item"
      >
        {Object.keys(historyItem.modifications).map((key, index) => {
          return (
            <li
              key={`history-item-${key}-${index}`}
              dangerouslySetInnerHTML={{
                __html: historyItem.modifications[key][2],
              }}
            />
          );
        })}
      </ul>
      <span> {t("by")} </span>
      <UserLink
        id={historyItem.created_by_id}
        name={historyItem.created_by_name}
        email={historyItem.created_by_email}
        hideAvatar
        short
        renderAs={Caption1}
        context="object-history"
      />
      ,
      <span>
        {" "}
        <Ago
          timestamp={historyItem.timestamp}
          className="bubble_date"
        />
      </span>
    </li>
  );
};

HistoryItem.propTypes = {
  historyItem: PropTypes.object,
};

const ObjectHistory = ({ objectType, objectId, projectId, alwaysShown, className }) => {
  const [showHistory, setShowHistory] = useState(false);

  const canSeeEstimate = useSelector((state) => canSeeTimeEstimates(state, projectId));
  const handleOnClick = useCallback(() => {
    setShowHistory(!showHistory);
  }, [showHistory]);

  const { t } = useTranslation();
  const { data, isLoading } = useHistoryOfChanges(objectType, objectId);

  const history = useMemo(() => {
    return data
      ?.filter((item) => {
        return !(objectType === taskClass && item?.modifications?.estimate && !canSeeEstimate);
      })
      .sort((a, b) => descSortCompare(a.timestamp, b.timestamp));
  }, [canSeeEstimate, data, objectType]);

  useEffect(() => {
    if (alwaysShown && !showHistory) {
      setShowHistory(true);
    }
  }, [alwaysShown, showHistory, setShowHistory]);

  const renderHistoryList = useCallback(() => {
    return (
      <ul className="object_history_list tw-my-0">
        {history.map((historyItem, index) => {
          return (
            <HistoryItem
              key={`history-${index}`}
              historyItem={historyItem}
            />
          );
        })}
      </ul>
    );
  }, [history]);

  return (
    <div
      className={classNames("object_history", className, {
        no_history: history && history.length === 0,
      })}
    >
      {isLoading && <SectionLoader className="tw-flex tw-flex-1 tw-mt-4" />}

      {alwaysShown ? (
        history && history.length > 0 ? (
          renderHistoryList()
        ) : (
          <div>
            <Body2>{t(`No history of changes for this object`)}</Body2>
          </div>
        )
      ) : (
        <>
          {history && history.length > 0 ? (
            <>
              <Button
                variant="option"
                size="large"
                onClick={handleOnClick}
              >
                <HistoryOfChangesIcon />
                {showHistory ? t("Hide History of Changes") : t("Show History of Changes")}
              </Button>

              {showHistory ? renderHistoryList() : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

ObjectHistory.propTypes = {
  history: PropTypes.array,
};

ObjectHistory.defaultProps = {
  history: [],
};

ObjectHistory.propTypes = {
  objectType: PropTypes.string,
  objectId: PropTypes.number,
  projectId: PropTypes.number,
  alwaysShown: PropTypes.bool,
  className: PropTypes.string,
};

export default ObjectHistory;
